import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, tap, map } from 'rxjs/operators';
import { NewsItem } from '../models/news-item.model';
import { NEWS_ENDPOINT} from '../app.constants';
import { PRODUCTS_ENDPOINT} from '../app.constants';

@Injectable({
    providedIn: 'root'
})

export class NewsService {

    public selectedNewsItem = new BehaviorSubject<NewsItem>(null);
    public totalResults = new Subject<string>();
    private readonly newsEndpoint = NEWS_ENDPOINT;
    private readonly productsEndpoint = PRODUCTS_ENDPOINT;

    constructor(private http: HttpClient) {}

    getGeneralNewsItems(pageNumber = 1, pageSize = 30, query = '', startDate = '', endDate = ''): Observable<NewsItem[]> {
        return this.http.get<{documents: NewsItem[]}>(this.newsEndpoint, {
            observe: 'response',
            params: this.setParams(pageNumber, pageSize, query, startDate, endDate)
        }).pipe(
            tap((response: HttpResponse<{ documents: NewsItem[] }>) => {
                this.totalResults.next(response.headers.get('X-Total-Count'));
            }),
            map((res) => 
                // Ensure `res.body` is safely typed as NewsItem[]
                (res.body.documents || []).sort((a: NewsItem, b: NewsItem) => b.publicationDate - a.publicationDate)
            ),
            shareReplay(1)
        );
    }

    getProductNewsItems(productId, pageNumber = 1, pageSize = 30, query = '', startDate = '', endDate = ''): Observable<NewsItem[]> {
        return this.http.get<NewsItem[]>(this.productsEndpoint + '/' + productId + '/news', {
            observe: 'response',
            params: this.setParams(pageNumber, pageSize, query, startDate, endDate)
        }).pipe(
            tap((response: HttpResponse<NewsItem[]>) => {
                this.totalResults.next(response.headers.get('X-Total-Count'));
            }),
            map(res => res.body),
            shareReplay(1)
        );
    }

    getNewsItem(newsId): Observable<NewsItem> {
        return this.http.get<NewsItem>(this.newsEndpoint + '/' + newsId).pipe(shareReplay(1));
    }

    getProductNews(
        productId: string,
        searchParams: unknown,
        size: number,
        page = 1
    ): Observable<HttpResponse<NewsItem[]>> {
        const params = Object.assign({
            size: String(size),
            page: String(page)
        }, searchParams);

        Object.keys(params).forEach(param => {
            if (!params[param]) {
                delete params[param];
            }
        });

        return this.http.get<NewsItem[]>(this.productsEndpoint + '/' + productId + '/news', {
            observe: 'response',
            params
        });
    }

    setParams(pageNumber: number, pageSize: number, query: string, startDate: string, endDate: string): {[key: string]: string | number} {
        const params: {
            [key: string]: string | number;
        } = {};

        params.page = String(pageNumber);
        params.size = String(pageSize);

        if (query) {
            params.query = query;
        }
        if (startDate) {
            params.fromDateTime = new Date(`${startDate}T00:00:00`).getTime();
        }
        if (endDate) {
            params.toDateTime = new Date(`${endDate}T23:59:59`).getTime();
        }

        return params;
    }

}
